import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import { sessionRegistrationsService, usersService } from '../../services/firestoreService';
import './AdminLayout.css';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useUser();
  const { sessions, courses, homeworks } = useData();

  const stats = useMemo(() => {
    const activeSessions = sessions.filter((session) => {
      const start = new Date(session.startDate);
      const end = new Date(session.endDate);
      const now = new Date();
      return now >= start && now <= end;
    }).length;

    const upcomingSessions = sessions.filter((session) => new Date(session.startDate) > new Date()).length;
    const gradedHomework = homeworks.filter((hw) => hw.status === 'graded').length;

    return [
      { label: 'Active Sessions', value: activeSessions, trend: `${courses.length} courses` },
      { label: 'Upcoming Sessions', value: upcomingSessions, trend: 'Next 30 days' },
      { label: 'Homework Items', value: homeworks.length, trend: `${gradedHomework} graded` },
    ];
  }, [sessions, courses, homeworks]);

  const quickActions = useMemo(() => [
    { label: `Pending Approvals (${sessionRegistrationsService?.pendingCount || '…'})`, icon: '✅' },
    { label: `Total Students (${usersService?.cachedStudentCount || '…'})`, icon: '👥' },
    { label: 'Launch pop-up event', icon: '🎪' },
    { label: 'Review safety logs', icon: '🛡️' },
  ], []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Navigate anyway
      navigate('/');
    }
  };

  const navItems = [
    { path: '/admin/courses', label: 'Courses', icon: '📚' },
    { path: '/admin/sessions', label: 'Sessions', icon: '📅' },
    { path: '/admin/homework', label: 'Homework', icon: '📝' },
    { path: '/admin/students', label: 'Students', icon: '👥' },
    { path: '/admin/interactive-learning', label: 'Content Studio', icon: '🎮' },
    { path: '/admin/add-python-course', label: 'Add Python Course', icon: '🐍' },
    { path: '/admin/add-advanced-courses', label: 'Add Advanced Courses', icon: '🚀' },
    { path: '/admin/add-new-course', label: 'Add New Course', icon: '📚' },
  ];

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-header">
          <h1>🎓 Admin Panel</h1>
          <p>Welcome, {user?.name}!</p>
        </div>
        <nav className="admin-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
        </nav>
        <button className="signout-btn" onClick={handleSignOut}>
          🚪 Sign Out
        </button>
      </aside>
      <main className="admin-content">
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Operations control</p>
            <h2>Hey {user?.name || 'Admin'}, ready to inspire today?</h2>
          </div>
          <button type="button" className="primary-cta">
            ➕ Schedule session
          </button>
        </header>
        <section className="admin-stats-grid">
          {stats.map((stat) => (
            <article key={stat.label} className="admin-stat-card">
              <p className="stat-label">{stat.label}</p>
              <p className="stat-value">{stat.value}</p>
              <p className="stat-trend">{stat.trend}</p>
            </article>
          ))}
        </section>
        <section className="admin-quick-actions">
          {quickActions.map((action) => (
            <button key={action.label} type="button" className="action-chip">
              <span aria-hidden="true">{action.icon}</span>
              {action.label}
            </button>
          ))}
        </section>
        <section className="admin-panel">
          {children}
        </section>
      </main>
    </div>
  );
};

export default AdminLayout;

